<template>
    <v-menu v-model="snapshotMenu" offset-x v-if="showSnapshotMenu" nudge-top="8" :close-on-content-click="false" min-width="300">
        <template v-slot:activator="{ on: menuOn }">
            <v-tooltip right :disabled="!collapsed">
                <template v-slot:activator="{ on: tooltipOn }">
                    <v-list-item
                        v-on="{ ...menuOn, ...tooltipOn }"
                        @mouseenter="isMenuActivatorHovered = true"
                        @mouseleave="isMenuActivatorHovered = false"
                        :disabled="fetchingSnapshots"
                        :input-value="Boolean(activeSnapshotSnid)"
                        active-class="primary--text">
                        <v-list-item-icon>
                            <v-icon :color="selectedSnapshotIndicator.color">
                                {{ isMenuActivatorHovered ? 'mdi-chevron-right' : selectedSnapshotIndicator.icon }}
                            </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content v-if="fetchingSnapshots">
                            <v-progress-linear indeterminate rounded></v-progress-linear>
                        </v-list-item-content>
                        <v-list-item-content v-else>
                            <v-list-item-title :class="{ 'text--disabled': isSpaceRested || isSpaceArchived }">
                                <b>{{ snapshotName }}</b>
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="showQuickActions && !collapsed && isInstanceEditor && quickActionSnapshot" class="my-0">
                            <v-menu v-model="snapshotActionsMenu" offset-x right nudge-top="10">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn text small class="px-2" min-width="0" v-bind="attrs" v-on="on" @click.stop.prevent>
                                        <v-icon small>more_horiz</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense nav>
                                    <snapshot-restore-dialog
                                        v-if="!isSpaceArchived && quickActionSnapshot && canRestoreToCurrent"
                                        :snapshotData="quickActionSnapshot"
                                        :isRestoreToCurrentInstance="true">
                                        <template v-slot:activator="{ attrs, open }">
                                            <v-list-item v-bind="attrs" @click.stop="open">
                                                <v-list-item-icon>
                                                    <v-icon>restore</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="mr-4">Reset to current state</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </snapshot-restore-dialog>
                                    <v-list-item v-else-if="!isSpaceArchived" disabled>
                                        <v-list-item-icon>
                                            <v-icon>restore</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Reset to current state</v-list-item-title>
                                    </v-list-item>

                                    <snapshot-restore-dialog
                                        v-if="!isSpaceArchived && quickActionSnapshot && canRestoreToAnother"
                                        :snapshotData="quickActionSnapshot"
                                        :isRestoreToCurrentInstance="false">
                                        <template v-slot:activator="{ attrs, open }">
                                            <v-list-item v-bind="attrs" @click.stop="open">
                                                <v-list-item-icon>
                                                    <v-icon>group</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="mr-4">Reset to another instance</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </snapshot-restore-dialog>
                                    <v-list-item v-else-if="!isSpaceArchived" disabled>
                                        <v-list-item-icon>
                                            <v-icon>group</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Reset to another instance</v-list-item-title>
                                    </v-list-item>

                                    <v-divider v-if="!isSpaceArchived" class="my-2"></v-divider>

                                    <entity-delete-dialog
                                        v-if="quickActionSnapshot"
                                        entity-type="snapshot"
                                        :snapshotData="[quickActionSnapshot]"
                                        @deleting="snapshotActionsMenu = false">
                                        <template v-slot:activator="{ attrs, open }">
                                            <v-list-item v-bind="attrs" :disabled="isInstanceArchived && !isSpaceAdmin" @click.stop="open">
                                                <v-list-item-icon>
                                                    <v-icon color="error">mdi-trash-can-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="error--text mr-4">Delete snapshot</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </entity-delete-dialog>
                                    <v-list-item v-else disabled>
                                        <v-list-item-icon>
                                            <v-icon color="grey">mdi-trash-can-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Delete snapshot</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </v-list-item-action>
                    </v-list-item>
                </template>
                <span>{{ snapshotName }}</span>
            </v-tooltip>
        </template>

        <ContextSelectorList
            level="snapshot"
            :open="snapshotMenu"
            :selected-id="$route.params.snid"
            @select="openSnapshot($event.snid)"
            @view-all="goToSnapshots" />
    </v-menu>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { snapshotEnums } from '@/mixins/snapshot'
import { enumsData } from '@/mixins/enums'
import ContextSelectorList from '@/components/contextSelectors/ContextSelectorList.vue'

const EntityDeleteDialog = () => import('@/components/EntityDeleteDialog.vue')
const SnapshotRestoreDialog = () => import('@/modules/snapshot/components/TheSnapshotRestore.vue')

export default {
    name: 'SnapshotMenu',
    components: {
        ContextSelectorList,
        EntityDeleteDialog,
        SnapshotRestoreDialog
    },
    mixins: [snapshotEnums, enumsData],
    props: {
        collapsed: {
            type: Boolean,
            default: false
        },
        showQuickActions: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            snapshotMenu: false,
            snapshotActionsMenu: false,
            isMenuActivatorHovered: false
        }
    },
    computed: {
        ...mapGetters('instanceStore', ['isInstanceEditor', 'isDistributedInstance', 'isInstanceArchived']),
        ...mapState('instanceStore', ['instanceSnapshots', 'fetchingSnapshots']),
        ...mapState('orgStore', ['fetchingSpaces']),
        ...mapState('spaceStore', ['fetchingInstances']),
        ...mapGetters('snapshotStore', ['isDevelopment']),
        ...mapGetters('spaceStore', [
            'isSpaceArchived',
            'isSpaceRested',
            'isTrialSpace',
            'isSpaceAdmin',
            'isSpaceInAnyRestState',
            'isSpaceInAnyRestStateExceptPreResting'
        ]),
        snapshotName() {
            if (this.isSpaceArchived && this.isDevelopment) {
                return 'Archived'
            }
            if (this.isSpaceRested && this.isDevelopment) {
                return 'Resting'
            }
            if (this.$route.params.snid !== undefined && this.instanceSnapshots && this.instanceSnapshots.length) {
                return this.$store.getters['snapshotStore/snapshotLongNameById'](this.$route.params.snid)
            }
            return 'select snapshot'
        },
        activeSnapshotSnid() {
            return this.$route.params.snid
        },
        currentState() {
            return this.instanceSnapshots.filter(snapshot => snapshot.long_id === 'Current State')
        },
        selectedSnapshot() {
            if (this.$route.params.snid === undefined || !this.instanceSnapshots.length) {
                return null
            }
            return this.instanceSnapshots.find(snapshot => snapshot.snid.toString() === this.$route.params.snid.toString()) || null
        },
        isCurrentStateSelected() {
            return Boolean(this.currentState.length && this.$route.params.snid && this.currentState[0].snid.toString() === this.$route.params.snid.toString())
        },
        selectedSnapshotIndicator() {
            if (!this.selectedSnapshot && !this.isCurrentStateSelected) {
                return {
                    icon: 'mdi-circle',
                    color: 'secondary'
                }
            }
            return this.getSnapshotIndicator(this.selectedSnapshot, { isCurrentState: this.isCurrentStateSelected })
        },
        isConfigRoute() {
            return (this.$route.name || '').startsWith('config')
        },
        showSnapshotMenu() {
            if (this.isConfigRoute && this.$route.params.iid !== undefined && !this.fetchingSnapshots && !this.fetchingInstances && !this.fetchingSpaces) {
                return true
            }
            if (this.instanceSnapshots.length && !this.fetchingSnapshots && !this.fetchingInstances && !this.fetchingSpaces) {
                return true
            }
            return false
        },
        quickActionSnapshot() {
            if (!this.selectedSnapshot || this.isCurrentStateSelected) {
                return null
            }
            return this.selectedSnapshot
        },
        canRestoreToCurrent() {
            return this.isInstanceEditor && !this.isDistributedInstance && !this.isSpaceInAnyRestStateExceptPreResting
        },
        canRestoreToAnother() {
            return this.isInstanceEditor && !this.isDistributedInstance
        }
    },
    methods: {
        openSnapshot(snid) {
            this.snapshotMenu = false
            this.$router.push({
                name: 'snapshot-overview',
                params: { oid: this.$route.params.oid, sid: this.$route.params.sid, iid: this.$route.params.iid, snid }
            })
        },
        goToSnapshots() {
            this.snapshotMenu = false
            this.$router.push({
                name: 'instance-snapshots',
                params: { ...this.$route.params }
            })
        }
    },
    watch: {
        '$route.params.iid': {
            immediate: true,
            handler(iid) {
                if (this.isConfigRoute && iid !== undefined) {
                    this.$store.dispatch('instanceStore/fetchInstanceSnapshots', iid)
                }
            }
        },
        '$route.name'() {
            if (this.isConfigRoute && this.$route.params.iid !== undefined) {
                this.$store.dispatch('instanceStore/fetchInstanceSnapshots', this.$route.params.iid)
            }
        }
    }
}
</script>
